/**
 * Copyright Warner Bros. Entertainment, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property
 * of Warner Bros. Entertainment, Inc. and its suppliers, if any.
 * The intellectual and technical concepts contained herein are
 * proprietary to Warner Bros. Entertainment, Inc. and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in process,
 * and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is
 * unlawful and strictly forbidden unless prior written permission is
 * obtained from Warner Bros. Entertainment, Inc.
 */

import {ReduceStore} from 'flux/utils';
import Immutable from 'immutable';

import {DeliveryConstants} from './delivery-actions';
import Validations from '../../../common/validations/validations';
import {Dispatcher} from '../../../flux-helpers';
import {FindInConstant} from '../../../utils/utils';

const DeliveryValidations = {
    notifications: {
        validations: [Validations.custom(
            function() {
                return 'There is an error in Delivery Notifications.';
            },
            function() {
                let isValid = true;
                let nameRules = [Validations.max(50), Validations.required, Validations.noSymbols, Validations.noNumbers];
                let emailRules = [Validations.email, Validations.max(100), Validations.required];
                store.getState().get('notifications').forEach(notification => {
                    let notificationName = notification.get('notificationName');
                    let email = notification.get('notificationEmail');
                    let validate = function(rules, value) {
                        rules.forEach(r => {
                            if (!r.validate(value)) {
                                isValid = false;
                            }
                        });
                    };
                    validate(nameRules, notificationName);
                    validate(emailRules, email);
                });
                return isValid;
            }
        )]
    }
};

class DeliveryStore extends ReduceStore {

    getFileFormatType(fileFormatId) {
        return FindInConstant(DeliveryConstants.FILE_FORMAT_TYPES, fileFormatId);
    }

    getImageDeliveryType(imageDeliveryid) {
        return FindInConstant(DeliveryConstants.IMAGE_DELIVERY_TYPES, imageDeliveryid);
    }

    getInitialState() {
        let state = Immutable.Map({
            notifications: Immutable.List(),
            profile: Immutable.Map(),
            fileFormats: Immutable.fromJS(DeliveryConstants.toArray('FILE_FORMAT_TYPES')),
            imageDeliveryFormats: Immutable.fromJS(DeliveryConstants.toArray('IMAGE_DELIVERY_TYPES'))
        });

        state = state.set('originalProfile', state.get('profile'));
        state = state.set('originalNotifications', state.get('notifications'));

        return state;
    }

    getValidations() {
        return Validations.validate(this.getState().get('notifications'), DeliveryValidations);
    }

    reduce(state, action) {
        switch (action.actionType) {

        case DeliveryConstants.CLEAR:
            state = this.getInitialState();
            break;

        case DeliveryConstants.NOTIFICATION.ADD:
            state = state.updateIn(['notifications'], notifications => {
                return notifications.push(Immutable.Map({
                    orderIn: notifications.size + 1
                }));
            });
            break;

        case DeliveryConstants.NOTIFICATION.CLONE:
            state = state.set('originalNotifications', action.notifications);
            break;

        case DeliveryConstants.NOTIFICATION.GET:
            state = state.merge({
                notifications: action.notifications,
                originalNotifications: action.notifications
            });
            let notificationsList = state.get('notifications');
            notificationsList.map((notification, i) => {
                state = state.mergeIn(['notifications', i], {
                    orderIn: i
                });
            });
            break;

        case DeliveryConstants.NOTIFICATION.REMOVE:
            state = state.updateIn(['notifications'], notifications => {
                return notifications.delete(action.index);
            });
            break;

        case DeliveryConstants.NOTIFICATION.UPDATE:
            state = state.update('notifications', notifications => {
                return notifications.map((n, i) => {
                    if (i === action.orderIn) {
                        n = n.set(...action.attr.split('.'), action.value);
                    }
                    return n;
                });
            });
            break;

        case DeliveryConstants.PROFILE.CLEAR:
            state = state.merge({
                profile: Immutable.Map(),
                originalProfile: Immutable.Map()
            });
            break;

        case DeliveryConstants.PROFILE.CLONE:
            state = state.set('originalProfile', action.profile);
            break;

        case DeliveryConstants.PROFILE.GET:
            state = state.merge({
                profile: action.profile,
                originalProfile: action.profile
            });
            break;

        case DeliveryConstants.PROFILE.UPDATE:
            state = state.setIn(['profile', ...action.attr.split('.')], action.value);
            break;

        case DeliveryConstants.SET.VALIDATIONS:
            state = state.merge({
                validations: action.validations
            });
            break;
        }

        return state;
    }
}

const store = new DeliveryStore(Dispatcher);

export default store;
export {
    DeliveryValidations
};
